import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  UserPlus,
  Download,
  Upload
} from 'lucide-react';
import ContactModal from '../components/ContactModal';

const Contacts = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Données simulées
  const [contacts, setContacts] = useState([
    {
      id: 1,
      nom: 'Dupont',
      prenom: 'Jean',
      telephone: '+33123456789',
      email: 'jean.dupont@email.com',
      entreprise: 'TechCorp',
      date_creation: '2024-01-15'
    },
    {
      id: 2,
      nom: 'Martin',
      prenom: 'Marie',
      telephone: '+33987654321',
      email: 'marie.martin@email.com',
      entreprise: 'DesignStudio',
      date_creation: '2024-02-20'
    },
    {
      id: 3,
      nom: 'Bernard',
      prenom: 'Pierre',
      telephone: '+33555666777',
      email: 'pierre.bernard@email.com',
      entreprise: 'MarketingPro',
      date_creation: '2024-03-10'
    },
  ]);

  const filteredContacts = contacts.filter(contact =>
    contact.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.telephone.includes(searchTerm) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setIsModalOpen(true);
  };

  const handleDelete = (contactId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) {
      setContacts(contacts.filter(c => c.id !== contactId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedContacts(filteredContacts.map(c => c.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (contactId, checked) => {
    if (checked) {
      setSelectedContacts([...selectedContacts, contactId]);
    } else {
      setSelectedContacts(selectedContacts.filter(id => id !== contactId));
    }
  };

  const handleSaveContact = (contactData) => {
    if (editingContact) {
      setContacts(contacts.map(c => 
        c.id === editingContact.id ? { ...c, ...contactData } : c
      ));
    } else {
      const newContact = {
        ...contactData,
        id: Date.now(),
        date_creation: new Date().toISOString().split('T')[0]
      };
      setContacts([...contacts, newContact]);
    }
    setIsModalOpen(false);
    setEditingContact(null);
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Contacts</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Gérez votre base de contacts
          </p>
        </div>
        <div className="flex space-x-3">
          <button className="btn btn-secondary">
            <Upload className="h-4 w-4 mr-2" />
            Importer
          </button>
          <button className="btn btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setIsModalOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau contact
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Rechercher un contact..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn btn-secondary">
              <Filter className="h-4 w-4 mr-2" />
              Filtres
            </button>
            {selectedContacts.length > 0 && (
              <button className="btn btn-danger">
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer ({selectedContacts.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tableau des contacts */}
      <div className="card">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>Nom</th>
                <th>Téléphone</th>
                <th>Email</th>
                <th>Entreprise</th>
                <th>Date d'ajout</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => (
                <tr key={contact.id}>
                  <td>
                    <input
                      type="checkbox"
                      className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={(e) => handleSelectContact(contact.id, e.target.checked)}
                    />
                  </td>
                  <td className="font-medium text-secondary-900">
                    {contact.prenom} {contact.nom}
                  </td>
                  <td>{contact.telephone}</td>
                  <td>{contact.email}</td>
                  <td>{contact.entreprise || '-'}</td>
                  <td>{new Date(contact.date_creation).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(contact)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de contact */}
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingContact(null);
        }}
        onSave={handleSaveContact}
        contact={editingContact}
      />
    </div>
  );
};

export default Contacts;
